// AI Technical Response Generator v9.0 — Tender-Ready Professional Engineering Document Format
// Generates structured engineering documents: RFQ Draft + Technical Response (7-section)
// Enhanced for tender submission with client-specific language, project condition references,
// consistent formatting, and professional engineering tone.

import { generateText } from 'ai';
import { createOpenAI } from '@ai-sdk/openai';
import { RFQState } from './rfqConfig';
import { runRuleEngine, formatRuleEngineOutput, formatRFQData } from './ruleEngine';

export interface GeneratedOutput {
  rfqDraft: string;
  technicalResponse: string;
  referenceId: string;
  generatedAt: string;
  ruleEngineOutput: string;
}

// AI Configuration
const AI_CONFIG = {
  model: 'deepseek-v3',
  temperature: 0.3,
  maxTokens: 8000,
  system_prompt: `You are a Senior Operations Engineer at EnergiPro Solutions with 25+ years in GCC/MENA upstream oil & gas. You write tender-grade engineering documents — not sales material. Your documents are included directly in tender submissions and sent to client technical evaluation committees.

Writing principles:
- State facts. Do not hedge with "we recommend" or "we suggest". Use declarative language: "Configuration requires", "System includes", "Operations will proceed".
- Group equipment by functional system (e.g., "Surface Testing System", "Sand Management System", "Safety & Gas Detection").
- Justify every selection with engineering reasoning tied to the stated project conditions.
- Reference applicable standards (API 6A, API RP 14E, ASME B31.3, NACE MR0175, ISO 14001) where relevant.
- Never include pricing, commercial terms, or cost estimates.
- Never use marketing language or superlatives.
- Write in present tense, active voice.

TENDER-SPECIFIC REQUIREMENTS:
- Reference the client's specific project conditions (pressure, flow rates, H₂S, sand, location) explicitly throughout.
- Use consistent section numbering and formatting suitable for formal document packages.
- Maintain professional engineering tone — concise, declarative, standards-referenced.
- Include clear assumptions and clarification requirements to demonstrate engineering rigor.
- State compliance basis for each major decision (e.g., "Selected per API 6A pressure class requirements for stated 8,500 PSI wellhead pressure").
- Address operational risks proactively with mitigations documented.
- Ensure document reads as a standalone submission-ready engineering response.`,
};

// Build combined prompt for single API call
function buildCombinedPrompt(state: RFQState): string {
  const ruleOutput = runRuleEngine(state);
  const rfqData = formatRFQData(state);

  // Group equipment by category for the prompt
  const primaryEquip = ruleOutput.equipmentList.filter(e => e.category === 'primary');
  const safetyEquip = ruleOutput.equipmentList.filter(e => e.category === 'safety');
  const supportEquip = ruleOutput.equipmentList.filter(e => e.category === 'support');
  const logisticsEquip = ruleOutput.equipmentList.filter(e => e.category === 'logistics');

  const equipmentSummary = [
    primaryEquip.length > 0 ? `Primary Systems:\n${primaryEquip.map(e => `  - ${e.name}: ${e.specification}`).join('\n')}` : '',
    safetyEquip.length > 0 ? `Safety Systems:\n${safetyEquip.map(e => `  - ${e.name}: ${e.specification}`).join('\n')}` : '',
    supportEquip.length > 0 ? `Support Systems:\n${supportEquip.map(e => `  - ${e.name}: ${e.specification}`).join('\n')}` : '',
    logisticsEquip.length > 0 ? `Logistics:\n${logisticsEquip.map(e => `  - ${e.name}: ${e.specification}`).join('\n')}` : '',
  ].filter(Boolean).join('\n\n');

  return `Generate TWO documents based on the following project input. Separate them with "---SECTION_BREAK---".

PROJECT INPUT:
${rfqData}

ENGINEERED EQUIPMENT (from rule engine):
Package Level: ${ruleOutput.packageLevel}
${equipmentSummary}

${ruleOutput.flags.length > 0 ? `ENGINEERING FLAGS:\n${ruleOutput.flags.map(f => `- ${f}`).join('\n')}` : ''}

${ruleOutput.technicalNotes.length > 0 ? `TECHNICAL NOTES:\n${ruleOutput.technicalNotes.map(n => `- ${n}`).join('\n')}` : ''}

Service Model: ${ruleOutput.serviceModelNote || 'Not specified'}

═══════════════════════════════════════════════════
DOCUMENT 1: CLIENT RFQ DRAFT
═══════════════════════════════════════════════════

Structure:
1. SERVICE REQUESTED — One-line declaration
2. OPERATIONAL CONDITIONS — Table format (Parameter | Value | Unit)
3. SCOPE DEFINITION — 3-4 lines covering environment, service model, duration
4. LOCATION & LOGISTICS — Country, location type, access considerations
5. REQUIRED TIMELINE — Start date expectation, mobilization window
6. SPECIAL REQUIREMENTS — Any H2S, sand, pressure, or offshore flags

Write as a formal request document. No bullets — use structured paragraphs and tables.

═══════════════════════════════════════════════════
DOCUMENT 2: PRELIMINARY TECHNICAL RESPONSE
═══════════════════════════════════════════════════

MANDATORY STRUCTURE (use these exact section numbers and titles):

1. EXECUTIVE SUMMARY
   Two to three sentences. State what EnergiPro will deliver, the key constraint that drives the configuration, and confidence level. Be direct. No filler.

2. PROJECT CONDITIONS
   Present as a formatted table:
   Parameter | Value | Engineering Implication
   Include all stated conditions and what each means for equipment selection.

3. SELECTED EQUIPMENT CONFIGURATION
   Group by functional system. Each system gets a heading and its components listed with specification. Format:
   
   [SYSTEM NAME]
   Component — Specification — Standard/Rating
   
   Do NOT list equipment as a flat bullet list.

4. ENGINEERING JUSTIFICATION
   For each major selection, explain WHY it was chosen based on the stated conditions. Reference the specific parameter that drove the decision. Use format:
   "[Equipment] selected because [condition] requires [capability] per [standard]."

5. OPERATIONAL EXECUTION MODEL
   Describe the execution sequence: mobilization → rig-up → operations → demobilization. Include crew composition if full-service. State operational assumptions.

6. ENGINEERING NOTES
   Critical warnings, limitations, or conditions that require client confirmation. These are flagged items. Write as direct statements, not suggestions.

7. ASSUMPTIONS & CLARIFICATIONS
   List what was assumed and what requires client confirmation before final engineering.

TENDER SUBMISSION FORMATTING:
- Every section must reference the specific project conditions stated above.
- Use client's operational parameters (pressure, flow, location) explicitly in justifications.
- Maintain consistent numbering and heading hierarchy throughout.
- Document must stand alone as a formal engineering submission.
- Include document reference number and date in header format.
- Conclude with clear next-steps statement.

FORMATTING RULES:
- No bullet-heavy lists. Use grouped sections with clear hierarchy.
- Tables where data is tabular.
- Declarative tone throughout. Write "The configuration requires..." not "We recommend..."
- Reference standards where applicable.
- No pricing, no commercial terms.
- This document must read as written by a field engineer, not generated by software.

CRITICAL: Output DOCUMENT 1, then "---SECTION_BREAK---" on its own line, then DOCUMENT 2.`;
}

// Generate both outputs in a single AI call
export async function generateAIProposal(state: RFQState): Promise<GeneratedOutput> {
  const startTime = Date.now();
  const refId = `EP-RFQ-${Date.now().toString(36).toUpperCase()}`;

  // Get rule engine output for display
  const ruleOutput = runRuleEngine(state);
  const ruleEngineText = formatRuleEngineOutput(ruleOutput);

  // Build combined prompt
  const prompt = buildCombinedPrompt(state);

  console.log('[REQUEST] AI Generation - Engineering Document Format:', {
    service: state.service,
    referenceId: refId,
    promptLength: prompt.length,
  });

  const config = globalThis.ywConfig?.ai_config?.proposal_generator || AI_CONFIG;

  const openai = createOpenAI({
    baseURL: 'https://api.youware.com/public/v1/ai',
    apiKey: 'sk-YOUWARE',
  });

  try {
    const { text } = await generateText({
      model: openai(config.model || AI_CONFIG.model),
      messages: [
        { role: 'system', content: AI_CONFIG.system_prompt },
        { role: 'user', content: prompt },
      ],
      temperature: AI_CONFIG.temperature,
      maxTokens: AI_CONFIG.maxTokens,
    });

    console.log('[SUCCESS] AI Output Generated:', {
      referenceId: refId,
      outputLength: text.length,
      processingTime: `${Date.now() - startTime}ms`,
    });

    // Split the response into two documents
    let rfqDraft = '';
    let technicalResponse = '';

    if (text.includes('---SECTION_BREAK---')) {
      const parts = text.split('---SECTION_BREAK---');
      rfqDraft = parts[0].trim();
      technicalResponse = parts[1]?.trim() || '';
    } else {
      const splitPatterns = ['DOCUMENT 2', 'PRELIMINARY TECHNICAL RESPONSE', '1. EXECUTIVE SUMMARY'];
      let splitIndex = -1;
      for (const pattern of splitPatterns) {
        const idx = text.indexOf(pattern);
        if (idx > 0) {
          splitIndex = idx;
          break;
        }
      }
      if (splitIndex > 0) {
        rfqDraft = text.substring(0, splitIndex).trim();
        technicalResponse = text.substring(splitIndex).trim();
      } else {
        rfqDraft = formatRFQData(state);
        technicalResponse = text;
      }
    }

    return {
      rfqDraft,
      technicalResponse,
      referenceId: refId,
      generatedAt: new Date().toISOString(),
      ruleEngineOutput: ruleEngineText,
    };
  } catch (error: any) {
    console.error('[ERROR] AI Generation failed:', {
      error: error.message,
      processingTime: `${Date.now() - startTime}ms`,
    });
    throw new Error(`AI Generation failed: ${error.message}`);
  }
}
